import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { ProjectId, Team, User } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { removalConfirmationContent } from 'components/organisms/resource-management/utils/resource-removal-confirmation-content';
import { useTranslate } from 'hooks/use-translation';
import { getProjectMembersQueryKey, useProjectsQuery } from 'queries';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { useUsersQuery } from 'queries/members';
import {
  useProjectUsersLiteQuery,
  useRemoveProjectUserLiteFromQueryState,
  useRemoveProjectUsersLiteMutation,
} from 'queries/project';
import { useRolesQuery } from 'queries/roles';
import { logger } from 'services';
import { getUserLabel } from 'utils/get-user-label';
import { UserRemovedFromProjectParams } from '../../listeners';

interface Props {
  projectId: number;
  userId: number;
  roleId?: number;
  referenceTeam?: Team;
  onConfirm: (params: UserRemovedFromProjectParams) => void;
}

interface Result {
  showConfirmation: () => void;
  canRemoveMember: boolean;
}

export const useUserRemovalConfirmation = ({
  projectId,
  userId,
  roleId,
  referenceTeam,
  onConfirm,
}: Props): Result => {
  const queryClient = useQueryClient();

  const { data: projects = [] } = useProjectsQuery();
  const { data: users } = useUsersQuery();
  const { data: roles = [] } = useRolesQuery();
  const { data: projectUsersLite = {} } = useProjectUsersLiteQuery();
  const { mutateAsync: removeProjectUserLite } =
    useRemoveProjectUsersLiteMutation();
  const removeProjectUserLiteFromQueryState =
    useRemoveProjectUserLiteFromQueryState();

  const translate = useTranslate();

  const remove = translate('remove');
  const userText = translate('user');
  const projectText = translate('project');
  const teamMemberText = translate('team_member').toLowerCase();

  const userRole = useMemo(
    () => roles.find((role) => role.roleId === roleId),
    [roleId, roles],
  );

  const currentUser = useMemo(
    () => users?.find((x) => userId === x.userId),
    [userId, users],
  );

  const noOfUserRecordsInProject = useMemo(
    () =>
      projectUsersLite[projectId]?.reduce(
        (count, { user }) => count + (user.userId === userId ? 1 : 0),
        0,
      ),
    [projectId, projectUsersLite, userId],
  );

  const canRemoveMember = useMemo(
    () => noOfUserRecordsInProject > 1,
    [noOfUserRecordsInProject],
  );

  const memberName = useMemo(
    () => (currentUser ? getUserLabel(currentUser) : userText),
    [currentUser, userText],
  );
  const projectName = useMemo(
    () =>
      projects.find((project) => project.projectId === projectId)
        ?.projectName ?? projectText,
    [projectId, projectText, projects],
  );

  const removeUserTexts = useMemo(
    () => ({
      success: {
        title: translate('invitation.team_member.removal.success.title'),
        message: translate('invitation.team_member.removal.success.content'),
      },
      error: {
        title: translate('failure'),
        message: translate('invitation.team_member.removal.failure.content'),
      },
    }),
    [translate],
  );
  const removeAllocationsTexts = useMemo(
    () => ({
      success: {
        title: translate(
          'invitation.team_member.removal_allocation.success.title',
        ),
        message: translate(
          'invitation.team_member.removal_allocation.success.content',
        ),
      },
      error: {
        title: translate('failure'),
        message: translate(
          'invitation.team_member.removal_allocation.failure.content',
        ),
      },
    }),
    [translate],
  );
  const revertToPlaceholderTexts = useMemo(
    () => ({
      success: {
        title: translate(
          'invitation.usassign_from_placeholder.removal.success.title',
          { teamName: referenceTeam?.teamName },
        ),
        message: translate(
          'invitation.usassign_from_placeholder.removal.success.content',
          { teamName: referenceTeam?.teamName },
        ),
      },
      error: {
        title: translate('failure'),
        message: translate(
          'invitation.usassign_from_placeholder.removal.failure.content',
          { teamName: referenceTeam?.teamName },
        ),
      },
    }),
    [referenceTeam?.teamName, translate],
  );
  const revertToPlaceholderAndRemoveTexts = useMemo(
    () => ({
      success: {
        title: translate(
          'invitation.usassign_from_placeholder_and_remove_user.removal.success.title',
          { teamName: referenceTeam?.teamName },
        ),
        message: translate(
          'invitation.usassign_from_placeholder_and_remove_user.removal.success.content',
          { teamName: referenceTeam?.teamName },
        ),
      },
      error: {
        title: translate('failure'),
        message: translate(
          'invitation.usassign_from_placeholder_and_remove_user.removal.failure.content',
          { teamName: referenceTeam?.teamName },
        ),
      },
    }),
    [referenceTeam?.teamName, translate],
  );

  const userRemovalConfirmationTexts = useMemo(
    () => ({
      title: translate(
        'resource_management.actions.remove_member.confirmation.title',
        {
          memberType: teamMemberText,
        },
      ),
      content: removalConfirmationContent(memberName, projectName, translate),
    }),
    [memberName, projectName, teamMemberText, translate],
  );

  const userDeleteHandler = useCallback(
    (user: User) =>
      removeProjectUserLite({
        projectId,
        projectUsers: [
          {
            userId,
            ...(referenceTeam
              ? {
                  roleId,
                  referenceTeamId: referenceTeam?.teamId,
                }
              : {}),
          },
        ],
      })
        .then(() => {
          removeProjectUserLiteFromQueryState(projectId, {
            user: { userId },
            role: userRole,
            referenceTeam,
          });
          queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
          queryClient.invalidateQueries(
            getProjectMembersQueryKey(ProjectId(projectId)),
          );
          onConfirm({
            projectId,
            userResource: {
              type: ResourceType.User,
              user,
              role: userRole,
              referenceTeam,
            },
          });
          if (referenceTeam != null) {
            if (canRemoveMember) {
              toastService(revertToPlaceholderTexts.success).success();
            } else {
              toastService(revertToPlaceholderAndRemoveTexts.success).success();
            }
          } else if (canRemoveMember) {
            toastService(removeAllocationsTexts.success).success();
          } else {
            toastService(removeUserTexts.success).success();
          }
        })
        .catch((error) => {
          logger.error('Error when removing user from RM', { error });
          if (referenceTeam != null) {
            if (canRemoveMember) {
              toastService(revertToPlaceholderTexts.error).success();
            } else {
              toastService(revertToPlaceholderAndRemoveTexts.error).success();
            }
          } else if (canRemoveMember) {
            toastService(removeAllocationsTexts.error).success();
          } else {
            toastService(removeUserTexts.error).success();
          }
        }),
    [
      canRemoveMember,
      onConfirm,
      projectId,
      queryClient,
      referenceTeam,
      removeAllocationsTexts.error,
      removeAllocationsTexts.success,
      removeProjectUserLite,
      removeProjectUserLiteFromQueryState,
      removeUserTexts.error,
      removeUserTexts.success,
      revertToPlaceholderAndRemoveTexts.error,
      revertToPlaceholderAndRemoveTexts.success,
      revertToPlaceholderTexts.error,
      revertToPlaceholderTexts.success,
      roleId,
      userId,
      userRole,
    ],
  );

  const showConfirmation = useCallback(() => {
    if (currentUser == null) {
      return;
    }

    // Show modal only if there's one instance of the user in the project
    if (canRemoveMember) {
      userDeleteHandler(currentUser);
    } else {
      confirm({
        ...userRemovalConfirmationTexts,
        okText: remove,
        okType: 'danger',
        onOk: async () => {
          await userDeleteHandler(currentUser);
        },
      });
    }
  }, [
    currentUser,
    canRemoveMember,
    userRemovalConfirmationTexts,
    remove,
    userDeleteHandler,
  ]);

  return {
    showConfirmation,
    canRemoveMember,
  };
};
